import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, FileCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { VerificationResults } from '../components/verification/VerificationResults';
import { VerificationStats } from '../components/verification/VerificationStats';
import { VerificationHeader } from '../components/verification/VerificationHeader';
import { useVerification } from '../hooks/useVerification';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';

const ITEMS_PER_PAGE = 10;

export const VerificationPage = () => {
  const { loading, error, results, stats, verify } = useVerification();
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { translations } = useLanguage();

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await verify();
    } finally {
      setIsVerifying(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  // Calculate pagination
  const totalPages = results ? Math.ceil(results.length / ITEMS_PER_PAGE) : 0;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResults = results ? results.slice(startIndex, startIndex + ITEMS_PER_PAGE) : null;

  return (
    <div className="space-y-6">
      <VerificationHeader />

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Verification Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleVerify}
            disabled={isVerifying}
            className={`
              flex items-center gap-3 px-8 py-4 rounded-full text-white 
              shadow-lg transition-all transform
              ${isVerifying 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600 hover:shadow-xl'
              }
            `}
          >
            {isVerifying ? (
              <>
                <RefreshCw className="animate-spin" size={24} />
                <span className="text-lg font-medium">
                  {translations?.verificationPage?.verifying || 'جاري التحقق...'}
                </span>
              </>
            ) : (
              <>
                <FileCheck size={24} />
                <span className="text-lg font-medium">
                  {translations?.verificationPage?.verifyNow || 'تحقق الآن'}
                </span>
              </>
            )}
          </motion.button>
          
          {!results && !isVerifying && (
            <p className="mt-4 text-gray-500">
              {translations?.verificationPage?.clickButton || 'اضغط على الزر للتحقق من ترابط البيانات بين الحجاج والباقات والرحلات'}
            </p>
          )}
        </motion.div>

        {/* Results Section */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8"
          >
            <VerificationStats stats={stats} />
          </motion.div>
        )}

        {paginatedResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <VerificationResults results={paginatedResults} />

            {/* Pagination Controls */}
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`p-1 rounded-lg ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight size={20} />
                </button>

                <span className="text-sm text-gray-700">
                  {translations?.pagination?.page || 'صفحة'} {currentPage}{' '}
                  {translations?.pagination?.of || 'من'} {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`p-1 rounded-lg ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft size={20} />
                </button>
              </div>

              <div className="text-sm text-gray-500">
                {results.length} {translations?.pagination?.results || 'نتيجة'}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};